import { AnchorHTMLAttributes } from 'react';
import { getWarpStyledBoldHtml } from '../SanitizedText/utils.js';
import { getTextFromTags } from './utils.js';
import sanitizeHtml from 'sanitize-html';

type LinkWithTags = {
    tag: string;
    link: string;
};

type SharedProps = {
    rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
    target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
};

export interface TextAndLinkProps extends LinkWithTags, SharedProps {
    text: string;
}

/** Renders the text before a link if it exists, and the link itself.
 *
 * @prop `text` - a string wrapped with custom tags which will be replaced by an anchor tag.
 * @prop `tag` - single tag that will replace the text
 * @prop `link` - The link to the web page. The same as an `href`.
 * @prop `rel` - optional. The relationship of the linked URL.
 * @prop `target` - optional. How to display the linked URL.
 * @returns
 */

const TextAndLink = (props: TextAndLinkProps) => {
    const { text, tag, link, rel, target } = props;
    const texts = getTextFromTags(tag, text);
    if (!texts) return null;
    const { beforeLinkText, linkText } = texts;

    const linkConfig = {
        allowedTags: ['a'],
        allowedAttributes: { a: ['href', 'rel', 'target', 'class'] },
    };

    const _rel = rel ? `rel=${rel}` : '';
    const _target = target ? `target=${target}` : '';
    const cleanLinkHtml = sanitizeHtml(
        `<a href=${link} ${_rel} ${_target} class="underline">${linkText}</a>`,
        linkConfig,
    );

    const warpStyledHTML = getWarpStyledBoldHtml(beforeLinkText);
    return (
        <>
            <span
                dangerouslySetInnerHTML={{
                    __html: `${warpStyledHTML}${cleanLinkHtml}`,
                }}
            ></span>
        </>
    );
};

/** The recursive component that will either keep rendering the same component or
 * end the recursion by returning the final text, if it exists.
 *
 * @prop `text` - a string wrapped with custom tags or no tags if it is the end of the recursion.
 * @prop `links` - a list of objects that contain a tag and the link which will turn the text param into a link.
 * @prop `rel` - optional. The relationship of the linked URL.
 * @prop `target` - optional. How to display the linked URL.
 * @returns A component that renders text with inlined links as defined by the props
 */

export interface TextWithInlineLinksProps extends SharedProps {
    text: string;
    links: LinkWithTags[];
}

const RecursiveInlineLinks = (props: TextWithInlineLinksProps) => {
    const { links, text, ...rest } = props;
    // Recursively render the links
    if (links?.length) {
        return <TextWithInlineLinks text={text} links={links} {...rest} />;
    }
    // End recursion

    const warpStyledHTML = getWarpStyledBoldHtml(text);
    return (
        <span
            dangerouslySetInnerHTML={{
                __html: warpStyledHTML,
            }}
        ></span>
    );
};

/** A react component that renders text and links inline in the text. The links and text are defined by the
 * custom tags. Each link object will have its custom tag reference and the link which will become the link.
 *
 * More defined in the `index.d.ts` file.
 */
const TextWithInlineLinks = (props: TextWithInlineLinksProps) => {
    const { text, links, ...rest } = props;
    const [head, ...tail] = links;
    if (!head) return null;
    const { tag, link } = head;
    const texts = getTextFromTags(tag, text);
    if (!texts) return null;
    const { afterLinkText } = texts;
    return (
        <>
            <TextAndLink text={text} tag={tag} link={link} rel={props.rel} target={props.target} />
            <RecursiveInlineLinks text={afterLinkText} links={tail} {...rest} />
        </>
    );
};

export default TextWithInlineLinks;
